import React from 'react';
import {
  Zap,
  Cpu,
  CircuitBoard,
  Microchip,
  Battery,
  Lightbulb,
  Users,
  BookOpen,
  Award,
  TrendingUp,
  Settings,
  Smartphone,
  Radio,
  Wifi,
  Star,
  Briefcase,
  Globe
} from 'lucide-react';
import '../Css_files/electronics.css';
import Navbar from './Navbar';
import Footer from './Footer';

const Electronics = () => {
  const courses = [
    {
      title: 'Complete Electronics Engineering Bootcamp',
      duration: '6 Months',
      description: 'Master the fundamentals of electronics with hands-on projects and interactive learning.',
      features: ['Circuit Design Basics', 'Embedded Systems', 'Power Electronics', 'Capstone Project']
    },
    {
      title: 'Advanced VLSI Design Masterclass',
      duration: '3 Months',
      description: 'Learn chip design and VLSI from industry experts with real-world case studies.',
      features: ['VLSI Tools Training', 'Digital Systems', 'Mini Research Projects', 'Industry Mentor Support']
    },
    {
      title: 'Power Electronics & Renewable Systems',
      duration: '4 Months',
      description: 'Specialized course on modern energy systems, conversion, and smart grid applications.',
      features: ['Power Conversion', 'Renewable Energy Systems', 'Motor Control', 'Smart Grid Technologies']
    }
  ];

  const instructors = [
    {
      name: 'Dr. Anil Kumar',
      position: 'Lead Trainer',
      specialization: 'VLSI Design & Digital Systems',
      experience: '18+ years of training & industry expertise',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Sunita Sharma',
      position: 'Senior Instructor',
      specialization: 'Power Electronics & Renewable Energy',
      experience: '15+ years of teaching & consulting',
      image: 'https://images.pexels.com/photos/3182821/pexels-photo-3182821.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Rajesh Gupta',
      position: 'Instructor',
      specialization: 'Embedded Systems & IoT',
      experience: '12+ years in training and industry projects',
      image: 'https://images.pexels.com/photos/3182804/pexels-photo-3182804.jpeg?auto=compress&cs=tinysrgb&w=300'
    }
  ];

  const specializations = [
    { name: 'Embedded Systems', icon: Microchip, description: 'Microcontrollers, firmware development, and real-time systems.' },
    { name: 'Power Electronics', icon: Battery, description: 'Power conversion, renewable energy systems, and motor drives.' },
    { name: 'Digital Electronics', icon: CircuitBoard, description: 'Digital circuits, FPGA programming, and logic design.' },
    { name: 'IoT & Sensors', icon: Cpu, description: 'Smart devices, connected systems, and IoT applications.' },
    { name: 'Circuit Design', icon: Zap, description: 'Hands-on PCB design, testing, and prototyping.' },
    { name: 'Communication Systems', icon: Radio, description: 'Wireless communication, networking, and 5G concepts.' }
  ];

  const tools = [
    {
      title: 'Virtual VLSI Lab',
      description: 'Access online chip design tools and semiconductor simulation platforms.',
      icon: <Microchip className="electronics-facility-icon" />
    },
    {
      title: 'Power Electronics Simulator',
      description: 'Practice with high-power conversion and renewable energy models virtually.',
      icon: <Battery className="electronics-facility-icon" />
    },
    {
      title: 'Embedded Systems Sandbox',
      description: 'Experiment with microcontrollers, IoT boards, and coding environments online.',
      icon: <Cpu className="electronics-facility-icon" />
    },
    {
      title: 'Circuit Design Studio',
      description: 'Design, test, and simulate circuits using industry-standard cloud platforms.',
      icon: <CircuitBoard className="electronics-facility-icon" />
    }
  ];

  const careerStats = [
    { icon: <Users />, value: '95%', label: 'Learner Satisfaction' },
    { icon: <Award />, value: '₹15 LPA', label: 'Top Salary Package' },
    { icon: <Briefcase />, value: '250+', label: 'Hiring Partners' },
    { icon: <Star />, value: '4.8/5', label: 'Course Rating' }
  ];

  const testimonials = [
    {
      name: 'Arjun Patel',
      role: 'Design Engineer, Intel',
      year: '2022 Learner',
      content: 'This online VLSI course gave me the confidence and skills to break into the semiconductor industry.',
      image: 'https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'Priya Singh',
      role: 'Embedded Engineer, Tesla',
      year: '2021 Learner',
      content: 'The IoT and embedded systems program was highly practical and helped me land my dream role in automotive tech.',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  return (
    <>
      <Navbar /><br /><br />
      <div className="electronics-page">
        {/* Hero Section */}
        <section className="electronics-hero">
          <div className="electronics-hero-background">
            <div className="electronics-animated-circuits">
              <div className="electronics-circuit electronics-circuit-1"></div>
              <div className="electronics-circuit electronics-circuit-2"></div>
              <div className="electronics-circuit electronics-circuit-3"></div>
              <div className="electronics-circuit electronics-circuit-4"></div>
            </div>
            <div className="electronics-floating-signals">
              <div className="electronics-signal electronics-signal-1"></div>
              <div className="electronics-signal electronics-signal-2"></div>
              <div className="electronics-signal electronics-signal-3"></div>
              <div className="electronics-signal electronics-signal-4"></div>
              <div className="electronics-signal electronics-signal-5"></div>
            </div>
          </div>
          <div className="electronics-hero-overlay"></div>
          <div className="electronics-hero-content">
            <div className="electronics-hero-logo">
              <div className="electronics-logo-circuit">
                <Zap className="electronics-logo-icon" />
              </div>
            </div>
            <h1 className="electronics-hero-title">
              Learn Electronics Engineering Online
              <span className="electronics-hero-subtitle">Master Skills. Build Innovation. Go Global.</span>
            </h1>
            <p className="electronics-hero-description">
              Gain in-demand electronics skills from anywhere in the world. Our online programs cover microchips, IoT,
              renewable energy, and more — designed for your career growth.
            </p>
            <div className="electronics-hero-actions">
              <button className="electronics-btn electronics-btn-primary">Enroll Now</button>
              <button className="electronics-btn electronics-btn-secondary">Download Syllabus</button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="electronics-stats">
          <div className="electronics-container">
            <div className="electronics-stats-grid">
              {careerStats.map((stat, index) => (
                <div key={index} className="electronics-stat-card">
                  <div className="electronics-stat-icon">{stat.icon}</div>
                  <div className="electronics-stat-value">{stat.value}</div>
                  <div className="electronics-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Courses Section */}
        <section className="electronics-programs">
          <div className="electronics-container">
            <div className="electronics-section-header">
              <h2 className="electronics-section-title">Popular Courses</h2>
              <p className="electronics-section-subtitle">
                Learn from beginner to advanced electronics with structured, industry-ready courses.
              </p>
            </div>
            <div className="electronics-programs-grid">
              {courses.map((course, index) => (
                <div key={index} className="electronics-program-card">
                  <div className="electronics-program-header">
                    <h3 className="electronics-program-title">{course.title}</h3>
                    <span className="electronics-program-duration">{course.duration}</span>
                  </div>
                  <p className="electronics-program-description">{course.description}</p>
                  <ul className="electronics-program-features">
                    {course.features.map((feature, idx) => (
                      <li key={idx} className="electronics-program-feature">
                        <span className="electronics-feature-bullet"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="electronics-program-btn">Explore Course</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Instructors Section */}
        <section className="electronics-faculty">
          <div className="electronics-container">
            <div className="electronics-section-header">
              <h2 className="electronics-section-title">Meet Your Instructors</h2>
              <p className="electronics-section-subtitle">
                Learn from experts with decades of teaching and real-world industry experience.
              </p>
            </div>
            <div className="electronics-faculty-grid">
              {instructors.map((trainer, index) => (
                <div key={index} className="electronics-faculty-card">
                  <div className="electronics-faculty-image">
                    <img src={trainer.image} alt={trainer.name} />
                    <div className="electronics-faculty-overlay">
                      <div className="electronics-faculty-experience">{trainer.experience}</div>
                    </div>
                  </div>
                  <div className="electronics-faculty-info">
                    <h3 className="electronics-faculty-name">{trainer.name}</h3>
                    <p className="electronics-faculty-position">{trainer.position}</p>
                    <p className="electronics-faculty-specialization">{trainer.specialization}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Specializations Section */}
        <section className="electronics-specializations">
          <div className="electronics-container">
            <div className="electronics-section-header">
              <h2 className="electronics-section-title">Specialization Paths</h2>
              <p className="electronics-section-subtitle">
                Choose your area of focus and build deep expertise with guided learning tracks.
              </p>
            </div>
            <div className="electronics-specializations-grid">
              {specializations.map((spec, index) => {
                const IconComponent = spec.icon;
                return (
                  <div key={index} className="electronics-specialization-card">
                    <div className="electronics-specialization-icon">
                      <IconComponent className="electronics-spec-icon" />
                    </div>
                    <h3 className="electronics-specialization-title">{spec.name}</h3>
                    <p className="electronics-specialization-description">{spec.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="electronics-facilities">
          <div className="electronics-container">
            <div className="electronics-section-header">
              <h2 className="electronics-section-title">Learning Tools & Platforms</h2>
              <p className="electronics-section-subtitle">
                Hands-on practice with world-class online labs and simulators.
              </p>
            </div>
            <div className="electronics-facilities-grid">
              {tools.map((tool, index) => (
                <div key={index} className="electronics-facility-card">
                  <div className="electronics-facility-icon-container">{tool.icon}</div>
                  <h3 className="electronics-facility-title">{tool.title}</h3>
                  <p className="electronics-facility-description">{tool.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="electronics-testimonials">
          <div className="electronics-container">
            <div className="electronics-section-header">
              <h2 className="electronics-section-title">Learner Success Stories</h2>
              <p className="electronics-section-subtitle">
                Hear from learners worldwide who have transformed their careers with us.
              </p>
            </div>
            <div className="electronics-testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="electronics-testimonial-card">
                  <div className="electronics-testimonial-content">
                    <p className="electronics-testimonial-text">"{testimonial.content}"</p>
                  </div>
                  <div className="electronics-testimonial-author">
                    <img src={testimonial.image} alt={testimonial.name} className="electronics-testimonial-avatar" />
                    <div className="electronics-testimonial-info">
                      <h4 className="electronics-testimonial-name">{testimonial.name}</h4>
                      <p className="electronics-testimonial-role">{testimonial.role}</p>
                      <p className="electronics-testimonial-year">{testimonial.year}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="electronics-cta">
          <div className="electronics-container">
            <div className="electronics-cta-content">
              <h2 className="electronics-cta-title">Start Your Learning Journey</h2>
              <p className="electronics-cta-description">
                Join our online electronics courses today and build the skills needed to power future technology.
              </p>
              <div className="electronics-cta-actions">
                <button className="electronics-btn electronics-btn-primary electronics-btn-large">Enroll Now</button>
                <button className="electronics-btn electronics-btn-outline electronics-btn-large">Try Free Class</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Electronics;
